import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CommunicationHistory = ({ communications, onSendMessage }) => {
  const [isComposing, setIsComposing] = useState(false);
  const [messageForm, setMessageForm] = useState({
    subject: '',
    message: '',
    type: 'email'
  });

  const getMessageIcon = (type) => {
    const icons = {
      'email': 'Mail',
      'phone': 'Phone',
      'sms': 'MessageSquare',
      'note': 'FileText'
    };
    return icons?.[type] || 'MessageCircle';
  };

  const getMessageColor = (type) => {
    const colors = {
      'email': 'text-blue-600 bg-blue-100',
      'phone': 'text-green-600 bg-green-100',
      'sms': 'text-purple-600 bg-purple-100',
      'note': 'text-orange-600 bg-orange-100'
    };
    return colors?.[type] || 'text-gray-600 bg-gray-100';
  };

  const handleSendMessage = (e) => {
    e?.preventDefault();
    onSendMessage(messageForm);
    setMessageForm({ subject: '', message: '', type: 'email' });
    setIsComposing(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date?.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Header with Compose Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h3 className="text-lg font-medium text-foreground">Communication History</h3>
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setIsComposing(true)}
        >
          New Message
        </Button>
      </div>
      {/* Communication Timeline */}
      <div className="space-y-4">
        {communications?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="MessageCircle" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">No Communications</h4>
            <p className="text-muted-foreground">Start a conversation with this candidate.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {communications?.map((comm) => (
              <div key={comm?.id} className="border border-border rounded-lg p-4">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getMessageColor(comm?.type)}`}>
                    <Icon name={getMessageIcon(comm?.type)} size={20} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-foreground">{comm?.subject || comm?.type?.charAt(0)?.toUpperCase() + comm?.type?.slice(1)}</h4>
                        <span className="text-xs text-muted-foreground capitalize px-2 py-1 bg-muted rounded-full">
                          {comm?.type}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>{comm?.sender}</span>
                        <span>{formatDate(comm?.date)}</span>
                      </div>
                    </div>
                    
                    <div className="text-sm text-foreground mb-3">
                      {comm?.message}
                    </div>

                    {comm?.attachments && comm?.attachments?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {comm?.attachments?.map((attachment, index) => (
                          <div key={index} className="flex items-center gap-2 px-3 py-1 bg-muted rounded-md text-xs">
                            <Icon name="Paperclip" size={12} />
                            <span>{attachment}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="Clock" size={12} />
                      <span>{comm?.time}</span>
                      {comm?.status && (
                        <>
                          <span>•</span>
                          <span className={comm?.status === 'read' ? 'text-green-600' : 'text-yellow-600'}>
                            {comm?.status}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Compose Message Modal */}
      {isComposing && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-2xl">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-foreground">New Message</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setIsComposing(false)}
                />
              </div>
            </div>

            <form onSubmit={handleSendMessage} className="p-6 space-y-4">
              {/* Message Type */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Message Type
                </label>
                <select
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  value={messageForm?.type}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, type: e?.target?.value }))}
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                  <option value="note">Internal Note</option>
                </select>
              </div>

              {/* Subject (for email) */}
              {messageForm?.type === 'email' && (
                <Input
                  label="Subject"
                  type="text"
                  placeholder="Enter email subject"
                  value={messageForm?.subject}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, subject: e?.target?.value }))}
                  required
                />
              )}

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Message *
                </label>
                <textarea
                  rows={6}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                  placeholder="Type your message here..."
                  value={messageForm?.message}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, message: e?.target?.value }))}
                  required
                />
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsComposing(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  iconName="Send"
                  iconPosition="left"
                >
                  Send Message
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunicationHistory;